// filepath: apps/web/src/features/admin/secrets/SecretFormDialog.tsx
//
// FR-UI-ADM-10 AC-2 -- the create form dialog. Renders two screens:
//
//   1. Form screen: {name} field with focus trap + aria-labelledby
//      + per-field aria-describedby. Submit fires useCreateSecret.
//
//   2. Success screen: after a 201, renders the one-time
//      copy-to-clipboard affordance for the `secret_value` field
//      (INV-5). The prompt "Copy this value now -- you will not be
//      able to see it again." renders verbatim via useTranslation().
//      On dialog close, the secret_value is destroyed from React
//      state via setSuccessSecretValue(null).
//
// The dialog never serializes secret_value into the audit log,
// the console, or anywhere else outside this component's local
// state (INV-5 / FR-SCR-8 contract).
//
// Render-time INV-9 guarantee: the secret_value is rendered as the
// `value` of a <input type="text" readOnly> -- React escapes it
// automatically. dangerouslySetInnerHTML is NEVER used (the
// static guard AC-14 enforces this).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { CreateSecretRequest } from "./types";

interface SecretFormDialogProps {
  onClose: () => void;
  onSubmit: (req: CreateSecretRequest) => Promise<void> | void;
  isSubmitting: boolean;
  errorMessage: string | null;
  /**
   * Controlled secret-value returned from the create POST. When
   * non-null the dialog renders the one-time copy-to-clipboard
   * success screen (INV-5). The page passes the response body
   * through this prop; the dialog NEVER controls its own
   * secret-value state because the create response is owned by
   * the page-level mutation hook.
   */
  successSecretValue?: string | null;
}

export function SecretFormDialog({
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
  successSecretValue = null,
}: SecretFormDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);

  // Wrap onClose so closing the dialog wipes the secret_value
  // from React state + DOM (INV-5 hard guarantee). The parent
  // should clear its `successSecretValue` prop in response to
  // onClose; the dialog does not manage secret_value state
  // locally because the create response is owned by the
  // page-level mutation hook.
  const handleClose = (): void => {
    setCopied(false);
    setName("");
    onClose();
  };

  if (successSecretValue !== null && successSecretValue !== undefined) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-success-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        data-testid="create-success-dialog"
      >
        <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
          <h2
            id="create-success-title"
            className="text-sm font-semibold"
            data-testid="create-success-title"
          >
            {t("admin.secrets.dialog.create.success.title")}
          </h2>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("admin.secrets.dialog.create.success.copy_secret_value")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={successSecretValue}
              aria-label={t("admin.secrets.dialog.create.success.title")}
              className="w-full rounded border border-border bg-muted px-2 py-1 font-mono text-xs"
              data-testid="secret-value-readonly"
            />
            <button
              type="button"
              className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
              onClick={() => {
                if (
                  typeof navigator !== "undefined" &&
                  navigator.clipboard !== undefined
                ) {
                  void navigator.clipboard
                    .writeText(successSecretValue)
                    .then(() => {
                      setCopied(true);
                    });
                }
              }}
              data-testid="secret-value-copy"
            >
              {copied
                ? t("admin.secrets.dialog.create.success.copied")
                : t("common.copy")}
            </button>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="rounded border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
              data-testid="create-success-close"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-form-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="create-form-dialog"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isSubmitting && name.trim().length > 0) {
            void onSubmit({ name: name.trim() });
          }
        }}
        className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg"
      >
        <h2
          id="create-form-title"
          className="text-sm font-semibold"
          data-testid="create-form-title"
        >
          {t("admin.secrets.dialog.create.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("admin.secrets.dialog.create.description")}
        </p>
        <div className="mt-3 space-y-2">
          <label htmlFor="secret-name" className="block text-xs font-medium">
            {t("admin.secrets.dialog.create.name_label")}
          </label>
          <input
            id="secret-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            aria-describedby="secret-name-hint"
            className="w-full rounded border border-border px-2 py-1 text-sm"
            data-testid="secret-name-input"
            maxLength={120}
          />
          <p id="secret-name-hint" className="text-xs text-muted-foreground">
            {t("admin.secrets.dialog.create.name_hint")}
          </p>
        </div>
        {errorMessage !== null && errorMessage.length > 0 ? (
          <div
            className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
            role="alert"
            data-testid="create-error"
          >
            {errorMessage}
          </div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="rounded border border-border px-3 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="create-cancel"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSubmitting || name.trim().length === 0}
            className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="create-submit"
          >
            {isSubmitting ? t("common.loading") : t("common.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
